import { useCallback } from "react";
import _ from "lodash";

import {
  calculateSwitchTransactionSaldo,
  mergeSwitchItems,
  percentageToQuantity,
  transactionValueToEuro,
  transactionValueToPercentage,
  transactionValueToQuantity,
} from "../../../../../utils";
import { QTY_DECIMALS } from "../../../../../../../utils/utils";
import { DECIMAL_SCALE } from "../../../../../../RiskProfiling/components/StepContent/components/formElement/InputFormElement/InputFormElement";

/**
 * Pure calculation: given an euro amount, computes the corresponding percentage
 * relative to the switch-out item.
 */
const computeFromEuro = (transactionValueEuro, item, switchOutItem) => {
  const qty = transactionValueToQuantity(
    transactionValueEuro,
    (switchOutItem && mergeSwitchItems(item, switchOutItem)) || item,
    false
  );
  const switchOutQty = switchOutItem
    ? transactionValueToQuantity(switchOutItem.transaction_value, switchOutItem, false)
    : 0;
  const percentage = transactionValueToPercentage(
    qty,
    (switchOutItem && mergeSwitchItems(item, switchOutItem, () => switchOutQty)) || item,
    false
  );
  return { transactionValueEuro, transactionValuePercentage: percentage };
};

/**
 * Pure calculation: given a percentage, computes the corresponding euro amount
 * relative to the switch-out item.
 */
const computeFromPercentage = (value, item, switchOutItem) => {
  const transactionValuePercentage = _.round(value, 2);
  const switchOutQty = switchOutItem
    ? transactionValueToQuantity(switchOutItem.transaction_value, switchOutItem, false)
    : 0;
  const qty = percentageToQuantity(
    value,
    (switchOutItem && mergeSwitchItems(item, switchOutItem, () => switchOutQty)) || item,
    false
  );
  let transactionValueEuro = transactionValueToEuro(
    qty,
    (switchOutItem && mergeSwitchItems(item, switchOutItem)) || item,
    false
  );
  transactionValueEuro = transactionValueEuro && _.round(transactionValueEuro, DECIMAL_SCALE);
  return { transactionValueEuro, transactionValuePercentage };
};

/**
 * Builds the data patch for an item when its euro amount changes.
 */
const buildEuroChangeData = (item, euroValue, switchOutItem) => {
  const transactionValueEuro = _.round(euroValue, DECIMAL_SCALE);
  const { transactionValuePercentage } = computeFromEuro(transactionValueEuro, item, switchOutItem);

  return {
    transaction_value: transactionValueEuro,
    calculated: {
      ..._.get(item, "calculated", {}),
      transaction_value_euro: transactionValueEuro,
      transaction_value_percentage: transactionValuePercentage,
    },
    errors: { ...item.errors, transaction_value: null, transaction_value_euro: null },
  };
};

/**
 * Switch-specific calculation handlers for switch plan trading.
 * Extends the base usePaymentPlanEdit hook with euro/percentage/distribute logic.
 *
 * @param {function} handleChange - The base handleChange from usePaymentPlanEdit
 * @param {object} portfolio - The portfolio object (needs maxQtyDecimals)
 * @param {object} switchOutsMap - map with existing switch outs
 */
export default function useSwitchCalculations(handleChange, portfolio, switchOutsMap) {
  const handleSwitchItemChange = useCallback((item, data, switchOutItem) => {
    // If it's a switch-out, delegate to base handleChange
    if (!switchOutItem) {
      handleChange(item, data);
      return;
    }
    // Switch-in: build the updated item
    const updatedItem = { ...item, ...data };
    _.unset(updatedItem, "errors.no_changes");
    handleChange(switchOutItem, {
      buy: switchOutItem.buy.map(b => {
        if (item.action === "create") {
          return b._id === item._id ? updatedItem : b;
        }
        return b.data.payment_id === item.data.payment_id ? updatedItem : b;
      }),
    });
  }, [handleChange]);

  const handleSwitchPercentageChanged = useCallback(
    (item, value, switchOutItem = undefined) => {
      const { transactionValueEuro, transactionValuePercentage } =
        computeFromPercentage(value, item, switchOutItem);

      handleSwitchItemChange(item, {
        transaction_value: transactionValueEuro,
        calculated: {
          ..._.get(item, "calculated", {}),
          transaction_value_percentage: transactionValuePercentage,
          transaction_value_euro: transactionValueEuro,
        },
        errors: { ...item.errors, transaction_value: null, transaction_value_euro: null },
      }, switchOutItem);
    },
    [handleSwitchItemChange]
  );

  const handleSwitchAmountEuroChanged = useCallback(
    (item, value, switchOutItem = undefined) => {
      handleSwitchItemChange(item, buildEuroChangeData(item, value, switchOutItem), switchOutItem);
    },
    [handleSwitchItemChange]
  );

  const handleDistributeQuantitiesClick = useCallback(
    (switchOutItem) => () => {
      const precision = portfolio.maxQtyDecimals || QTY_DECIMALS;
      const saldo = _.round(
        calculateSwitchTransactionSaldo(switchOutItem, portfolio.maxQtyDecimals),
        precision
      );

      if (saldo === 0 || _.isEmpty(switchOutItem.buy || [])) return;

      // Build all updated buy items in one pass
      let remainingSaldo = saldo;
      const updatedSwitchIns = switchOutItem.buy.map((transaction) => {
        if (remainingSaldo === 0) return transaction;

        let transactionValue = _.round((transaction.transaction_value || 0) + remainingSaldo, precision);

        if (transactionValue < 0) {
          remainingSaldo = _.round(remainingSaldo + (transaction.transaction_value || 0), precision);
          transactionValue = 0;
        } else {
          remainingSaldo = 0;
        }

        return {
          ...transaction,
          ...buildEuroChangeData(transaction, transactionValue, switchOutItem),
        };
      });

      // Single state update
      handleChange(switchOutItem, { buy: updatedSwitchIns });
    },
    [portfolio.maxQtyDecimals, handleChange]
  );

  const handleSwitchDiscountChange = useCallback(
    (item, value) => {
      const switchOutItem = item.switchOutItemId
        ? switchOutsMap[item.switchOutItemId]
        : undefined;

      handleSwitchItemChange(item, {
        discount: value,
        errors: { ...item.errors, discount: null },
      }, switchOutItem);
    },
    [handleSwitchItemChange, switchOutsMap]
  );

  return {
    handleSwitchPercentageChanged,
    handleSwitchAmountEuroChanged,
    handleSwitchDiscountChange,
    handleDistributeQuantitiesClick,
  };
}
